import { usePrivy } from "@privy-io/react-auth";
import { useQuery } from "@tanstack/react-query";
import { env } from "@/utils/envs";
import QueryKey from "@/utils/queryKeys";

export type RewardsPointsActivityEntry = {
	id: string;
	userId: string;
	points: number;
	source: string | null;
	sourceRef: string | null;
	description: string | null;
	metadata?: Record<string, unknown> | null;
	grantedAt: string;
};

export type RewardsPointsActivityResponse = {
	entries: RewardsPointsActivityEntry[];
	limit: number;
	offset: number;
	total?: number;
};

const normalizeResponse = (
	payload: unknown,
	limit: number,
	offset: number,
): RewardsPointsActivityResponse => {
	if (Array.isArray(payload)) {
		return {
			entries: payload as RewardsPointsActivityEntry[],
			limit,
			offset,
		};
	}

	if (!payload || typeof payload !== "object") {
		return {
			entries: [],
			limit,
			offset,
		};
	}

	const objectPayload = payload as {
		entries?: RewardsPointsActivityEntry[];
		activity?: RewardsPointsActivityEntry[];
		activities?: RewardsPointsActivityEntry[];
		items?: RewardsPointsActivityEntry[];
		total?: number;
		limit?: number;
		offset?: number;
	};

	return {
		entries:
			objectPayload.entries ??
			objectPayload.activities ??
			objectPayload.activity ??
			objectPayload.items ??
			[],
		total: objectPayload.total,
		limit: objectPayload.limit ?? limit,
		offset: objectPayload.offset ?? offset,
	};
};

export const usePointsActivity = (options?: {
	enabled?: boolean;
	limit?: number;
	offset?: number;
}) => {
	const { authenticated, ready, getAccessToken } = usePrivy();
	const limit = options?.limit ?? 20;
	const offset = options?.offset ?? 0;

	return useQuery({
		queryKey: [QueryKey.POINTS_ACTIVITY, limit, offset],
		queryFn: async (): Promise<RewardsPointsActivityResponse> => {
			const token = await getAccessToken();
			if (!token) throw new Error("Unable to retrieve access token");

			const response = await fetch(
				`${env.VITE_SHINKAI_WEB_API_URL}/api/rewards/points-activity?limit=${limit}&offset=${offset}`,
				{
					headers: { Authorization: `Bearer ${token}` },
				},
			);

			if (!response.ok) {
				throw new Error(
					`Failed to fetch points activity (${response.status})`,
				);
			}

			const payload = (await response.json()) as unknown;
			return normalizeResponse(payload, limit, offset);
		},
		enabled: (options?.enabled ?? true) && ready && authenticated,
		refetchOnWindowFocus: true,
	});
};
